function isWithin (v, a, b) {
  return v >= a && v <= b
}

function isPercent (v) {
  if (typeof v === 'string') {
    if (v.substr(-1) !== '%') {
      throw new Error('Missing %')
    }
    return isPercent(parseFloat(v))
  } else if (typeof v === 'number') {
    return isWithin(v, 0, 100)
  }
  throw new TypeError('Invalid type')
}

function toVariable (v, decimals = 2) {
  const string = v.toFixed(decimals)
  return string.replace(/\.0+$/, '')
}

function toPercent (v) {
  return v * 100
}

function fromPercent (v) {
  return v / 100
}

function isByte (v) {
  return isWithin(v, 0, 255)
}

function toByte (v) {
  return Math.round(v * 255)
}

function fromByte (v) {
  return v / 255
}

function toHexByte (v) {
  return toByte(v).toString(16).padStart(2, '0')
}

function fromHexByte (v) {
  return fromByte(parseInt(v, 16))
}

function fromRGBA (r, g, b, a) {
  return [fromByte(r), fromByte(g), fromByte(b), a]
}

function toHSLA (r, g, b, a) {
  let h, s
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  const l = ((max + min) * 0.5)
  if (delta === 0) {
    h = 0
    s = 0
  }
  if (r === max) {
    h = (60 * (((g - b) / delta) % 6))
  }
  if (g === max) {
    h = (60 * (((b - r) / delta) + 2))
  }
  if (b === max) {
    h = (60 * (((r - g) / delta) + 4))
  }
  s = (delta / (1 - Math.abs(2 * l - 1)))
  return [h, s, l, a]
}

function fromHSLA (h, s, l, a = 1) {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs((h / 60) % 2 - 1))
  const m = l - c * 0.5
  let r, g, b
  if (h >= 0 && h <= 60) {
    r = c
    g = x
    b = 0
  } else if (h >= 60 && h <= 120) {
    r = x
    g = c
    b = 0
  } else if (h >= 120 && h <= 180) {
    r = 0
    g = c
    b = x
  } else if (h >= 180 && h <= 240) {
    r = 0
    g = x
    b = c
  } else if (h >= 240 && h <= 300) {
    r = x
    g = 0
    b = c
  } else if (h >= 300 && h <= 360) {
    r = c
    g = 0
    b = x
  }
  return [
    r + m,
    g + m,
    b + m,
    a
  ]
}

function fromHexShort (values) {
  const r = fromHexByte(values.substr(0, 1) + values.substr(0, 1))
  const g = fromHexByte(values.substr(1, 1) + values.substr(1, 1))
  const b = fromHexByte(values.substr(2, 1) + values.substr(2, 1))
  return [r, g, b, 1]
}

function fromHexLong (values) {
  const r = fromHexByte(values.substr(0, 2))
  const g = fromHexByte(values.substr(2, 2))
  const b = fromHexByte(values.substr(4, 2))
  return [r, g, b, 1]
}

function fromHexAlpha (values) {
  const r = fromHexByte(values.substr(0, 2))
  const g = fromHexByte(values.substr(2, 2))
  const b = fromHexByte(values.substr(4, 2))
  const a = fromHexByte(values.substr(6, 2))
  return [r, g, b, a]
}

function parseHex (string) {
  if (string.substr(0, 1) !== '#') {
    throw new Error('Invalid hex color')
  }
  const values = string.substr(1)
  if (values.length === 3) {
    return fromHexShort(values)
  } else if (values.length === 6) {
    return fromHexLong(values)
  } else if (values.length === 8) {
    return fromHexAlpha(values)
  } else {
    throw new Error('Invalid hex color')
  }
}

const RE_NUMBER = '([-+]?[0-9]+(?:\\.[0-9]+)?)'

export function parseRGB (rgb) {
  const re = new RegExp(`rgb\\(${RE_NUMBER},${RE_NUMBER}%,${RE_NUMBER}%\\)`)
  const [, r, g, b] = rgb.match(re)
  return fromRGBA(parseFloat(r), parseFloat(g), parseFloat(b))
}

export function parseRGBA (rgb) {
  const re = new RegExp(`rgb\\(${RE_NUMBER},${RE_NUMBER}%,${RE_NUMBER}%\\)`)
  const [, r, g, b] = rgb.match(re)
  return fromRGBA(parseFloat(r), parseFloat(g), parseFloat(b))
}

export function parseHSL (hsl) {
  const re = new RegExp(`hsl\\(\\s*${RE_NUMBER}\\s*,\\s*${RE_NUMBER}%\\s*,\\s*${RE_NUMBER}%\\s*\\)`)
  const matches = hsl.match(re)
  if (!matches) {
    throw new Error('Cannot parse HSL')
  }
  const [, hue, saturation, lightness] = matches
  return fromHSLA(parseFloat(hue), parseFloat(saturation) / 100, parseFloat(lightness) / 100)
}

export function parseHSLA (hsla) {
  const re = new RegExp(`hsla\\(\\s*${RE_NUMBER}\\s*,\\s*${RE_NUMBER}%\\s*,\\s*${RE_NUMBER}%\\s*,\\s*${RE_NUMBER}\\s*\\)`)
  const matches = hsla.match(re)
  if (!matches) {
    throw new Error('Cannot parse HSL')
  }
  const [, hue, saturation, lightness, alpha] = matches
  return fromHSLA(parseFloat(hue), parseFloat(saturation) / 100, parseFloat(lightness) / 100, parseFloat(alpha))
}

export function parse (color) {
  if (color.substr(0, 1) === '#') {
    return parseHex(color)
  } else if (color.substr(0, 4) === 'rgba') {
    return parseRGBA(color)
  } else if (color.substr(0, 3) === 'rgb') {
    return parseRGB(color)
  } else if (color.substr(0, 4) === 'hsla') {
    return parseHSLA(color)
  } else if (color.substr(0, 3) === 'hsl') {
    return parseHSL(color)
  } else {
    throw new Error('Invalid color')
  }
}

export function stringify ([x, y, z, w], format = 'hex') {
  switch (format) {
    case 'hex': return `#${toHexByte(x)}${toHexByte(y)}${toHexByte(z)}`
    case 'hexa': return `#${toHexByte(x)}${toHexByte(y)}${toHexByte(z)}${toHexByte(w)}`
    case 'hsl': return `hsl(${x},${toPercent(y)}%,${toPercent(z)}%)`
    case 'hsla': return `hsla(${x},${toPercent(y)}%,${toPercent(z)}%,${w.toFixed(2)})`
    case 'rgb': return `rgb(${toByte(x)},${toByte(y)},${toByte(z)})`
    case 'rgba': return `rgba(${toByte(x)},${toByte(y)},${toByte(z)},${w.toFixed(2)})`
  }
}

export function translate (color, format = 'rgba') {
  const parsedColor = parse(color)
  const stringifiedColor = stringify(parsedColor, format)
  return stringifiedColor
}

export default {
  isWithin,
  isPercent,
  toPercent,
  toVariable,
  fromPercent,
  isByte,
  toByte,
  fromByte,
  toHSLA,
  fromHSLA,
  fromHexShort,
  fromHexLong,
  fromHexAlpha,
  parseHex,
  parseHSL,
  parseHSLA,
  parseRGB,
  parseRGBA,
  parse,
  stringify,
  translate
}
